/**
 * MultiSelectField Component
 *
 * A controlled multi-select autocomplete with chip tags. Same visual language
 * as AutocompleteField/DraftAutocompleteField, but with a plain
 * `value: Option[]` / `onChange(Option[])` API and no draft-state coupling —
 * for surfaces like notification subscriptions where the selection set IS the
 * data. Optional `groupBy` renders grouped options (e.g. resellers under
 * their parent company).
 */

import React from 'react';
import { Autocomplete, Box, Chip, TextField, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { colors, typography as typographyTokens, radii } from '../../tokens';
import type { AutocompleteOption } from './AutocompleteField';

export interface MultiSelectFieldProps {
    /** Field label */
    label: string;
    /** Currently selected options */
    value: AutocompleteOption[];
    /** Available options */
    options: AutocompleteOption[];
    /** Change handler with the full next selection */
    onChange: (selected: AutocompleteOption[]) => void;
    /** Placeholder shown when nothing is selected */
    placeholder?: string;
    /** Helper text displayed below the field */
    helperText?: string;
    /** Warning-toned helper line displayed below helperText */
    warningText?: string;
    /** Groups options under headers */
    groupBy?: (option: AutocompleteOption) => string;
    /** Allow free-typed entries (Enter/blur, comma- or semicolon-separated) to become chips */
    freeSolo?: boolean;
    /** Gate for free-typed entries; entries failing it are silently dropped (surface via warningText) */
    validateInput?: (rawValue: string) => boolean;
    /** Whether the field is disabled */
    disabled?: boolean;
    /** Whether options are loading */
    loading?: boolean;
    /** Whether the field is required */
    required?: boolean;
    /** Field size */
    size?: 'small' | 'medium';
    /** Whether the field takes full width */
    fullWidth?: boolean;
    /** Additional styles */
    sx?: SxProps<Theme>;
}

export const MultiSelectField: React.FC<MultiSelectFieldProps> = ({
    label,
    value,
    options,
    onChange,
    placeholder,
    helperText,
    warningText,
    groupBy,
    freeSolo = false,
    validateInput,
    disabled = false,
    loading = false,
    required = false,
    size = 'small',
    fullWidth = true,
    sx,
}) => (
    <Box sx={{ width: fullWidth ? '100%' : 'auto', ...sx }}>
        <Typography
            component='label'
            variant='body2'
            sx={{
                display: 'block',
                mb: '6px',
                fontWeight: typographyTokens.fontWeight.medium,
                color: colors.neutral[500],
                fontSize: typographyTokens.fontSize.sm,
            }}>
            {label}
            {required && <span style={{ color: colors.error.main, marginLeft: 4 }}>*</span>}
        </Typography>

        <Autocomplete
            multiple
            size={size}
            openOnFocus
            freeSolo={freeSolo}
            autoSelect={freeSolo}
            disabled={disabled}
            loading={loading}
            value={value}
            options={options}
            groupBy={groupBy}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
            filterSelectedOptions
            onChange={(_, selected) => {
                // freeSolo mixes typed strings into the selection; normalize them to
                // options, splitting pasted lists and dropping entries that fail validation
                const normalized = (selected as Array<AutocompleteOption | string>).flatMap((entry) => {
                    if (typeof entry !== 'string') return [entry];
                    return entry
                        .split(/[;,]/)
                        .map((rawValue) => rawValue.trim())
                        .filter(Boolean)
                        .filter((rawValue) => !validateInput || validateInput(rawValue))
                        .map((rawValue) => ({ id: rawValue.toLowerCase(), name: rawValue }));
                });
                const uniqueById = normalized.filter(
                    (option, index) => normalized.findIndex((other) => other.id === option.id) === index,
                );
                onChange(uniqueById);
            }}
            isOptionEqualToValue={(option, selected) => option.id === selected.id}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant='outlined'
                    placeholder={value.length ? undefined : placeholder ?? `Select ${label.toLowerCase()}`}
                    InputLabelProps={{ shrink: false }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            minHeight: 40,
                            fontSize: typographyTokens.fontSize.base,
                            backgroundColor: colors.neutral[0],
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: colors.neutral[300],
                                borderRadius: `${radii.md}px`,
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: colors.neutral[400],
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: colors.primary[500],
                                borderWidth: 2,
                            },
                            '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                                borderColor: colors.neutral[200],
                            },
                        },
                        '& .MuiInputBase-input::placeholder': {
                            color: colors.neutral[400],
                            opacity: 1,
                        },
                    }}
                />
            )}
            renderGroup={(params) => (
                <li key={params.key}>
                    <Typography
                        sx={{
                            px: '12px',
                            pt: '8px',
                            pb: '2px',
                            fontSize: typographyTokens.fontSize.xs,
                            fontWeight: typographyTokens.fontWeight.semibold,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            color: colors.neutral[400],
                        }}>
                        {params.group}
                    </Typography>
                    <ul style={{ padding: 0 }}>{params.children}</ul>
                </li>
            )}
            renderOption={(props, option) => (
                <li
                    {...props}
                    key={option.id}
                    style={{
                        fontSize: typographyTokens.fontSize.sm,
                        color: colors.neutral[700],
                        padding: '8px 12px',
                        paddingLeft: groupBy ? 20 : 12,
                    }}>
                    {option.name}
                </li>
            )}
            renderTags={(selected, getTagProps) =>
                selected.map((option, index) => (
                    <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={option.name}
                        size='small'
                        disabled={disabled}
                        sx={{
                            backgroundColor: colors.primary[100],
                            color: colors.primary[700],
                            fontWeight: typographyTokens.fontWeight.medium,
                            fontSize: typographyTokens.fontSize.xs,
                            borderRadius: `${radii.md}px`,
                            '& .MuiChip-deleteIcon': {
                                color: colors.primary[500],
                                '&:hover': {
                                    color: colors.primary[700],
                                },
                            },
                        }}
                    />
                ))
            }
            sx={{
                '& .MuiAutocomplete-paper': {
                    borderRadius: `${radii.md}px`,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                },
            }}
        />

        {helperText && (
            <Typography
                sx={{ mt: '6px', fontSize: typographyTokens.fontSize.xs, color: colors.neutral[500] }}>
                {helperText}
            </Typography>
        )}
        {warningText && (
            <Typography
                sx={{ mt: '4px', fontSize: typographyTokens.fontSize.xs, color: colors.warning.dark }}>
                {warningText}
            </Typography>
        )}
    </Box>
);

export default MultiSelectField;
